<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import Bs2Title from '@/components/common/layout/Bs2Title.vue'
import ButtonDaisy from '@/components/common/buttons/ButtonDaisy.vue'
import {
  useFeatureRegistryAdminStore,
  type FeatureAdmin,
  type PageAdmin,
  type FeatureDetail,
  type FeatureStatus,
  type SyncResult,
} from '@/stores/useFeatureRegistryAdminStore'
import {
  ArrowPathIcon,
  CubeTransparentIcon,
  ExclamationTriangleIcon,
  PencilSquareIcon,
  PlayIcon,
  RectangleStackIcon,
  SquaresPlusIcon,
} from '@heroicons/vue/24/outline'

const store = useFeatureRegistryAdminStore()

type Tab = 'features' | 'pages'
const activeTab = ref<Tab>('features')

// ─── Filtres ────────────────────────────────────────────────────────────────
const featureTypeFilter = ref<'' | 'PAGE' | 'CAPABILITY' | 'UMBRELLA'>('')
const featureStatusFilter = ref<'' | FeatureStatus>('')
const orphanOnlyFilter = ref(false)
const searchFilter = ref('')

const filteredFeatures = computed(() => {
  return store.features.filter((f) => {
    if (featureTypeFilter.value && f.type !== featureTypeFilter.value) return false
    if (featureStatusFilter.value && f.status !== featureStatusFilter.value) return false
    if (orphanOnlyFilter.value && !f.orphan) return false
    if (searchFilter.value) {
      const q = searchFilter.value.toLowerCase()
      if (
        !f.key.toLowerCase().includes(q) &&
        !f.label.toLowerCase().includes(q) &&
        !(f.category ?? '').toLowerCase().includes(q)
      ) {
        return false
      }
    }
    return true
  })
})

const filteredPages = computed(() => {
  return store.pages.filter((p) => {
    if (orphanOnlyFilter.value && !p.orphan) return false
    if (searchFilter.value) {
      const q = searchFilter.value.toLowerCase()
      if (
        !p.key.toLowerCase().includes(q) &&
        !p.label.toLowerCase().includes(q) &&
        !(p.featureKey ?? '').toLowerCase().includes(q)
      ) {
        return false
      }
    }
    return true
  })
})

// ─── Stats overview ─────────────────────────────────────────────────────────
const stats = computed(() => ({
  total: store.features.length,
  pages: store.features.filter((f) => f.type === 'PAGE').length,
  capabilities: store.features.filter((f) => f.type === 'CAPABILITY').length,
  umbrellas: store.features.filter((f) => f.type === 'UMBRELLA').length,
  orphans: store.features.filter((f) => f.orphan).length + store.pages.filter((p) => p.orphan).length,
}))

// ─── Édition Feature ────────────────────────────────────────────────────────
const showFeatureEditModal = ref(false)
const detail = ref<FeatureDetail | null>(null)
const isLoadingDetail = ref(false)
const updating = ref(false)
const updateError = ref<string | null>(null)

const editForm = ref({
  label: '',
  category: '',
  status: 'ACTIVE' as FeatureStatus,
})

async function openFeatureEdit(f: FeatureAdmin) {
  detail.value = null
  isLoadingDetail.value = true
  updateError.value = null
  showFeatureEditModal.value = true
  const fetched = await store.fetchFeatureDetail(f.uuid)
  if (fetched) {
    detail.value = fetched
    editForm.value = {
      label: fetched.label,
      category: fetched.category ?? '',
      status: fetched.status,
    }
  }
  isLoadingDetail.value = false
}

function closeFeatureEdit() {
  showFeatureEditModal.value = false
  detail.value = null
}

async function saveFeature() {
  if (!detail.value) return
  updating.value = true
  updateError.value = null
  try {
    const res = await store.updateFeature(detail.value.uuid, {
      label: editForm.value.label,
      category: editForm.value.category.trim() || null,
      status: editForm.value.status,
    })
    if (res.ok) {
      detail.value = { ...detail.value, ...res.data }
    } else {
      updateError.value = res.message
    }
  } finally {
    updating.value = false
  }
}

// ─── Édition Page ───────────────────────────────────────────────────────────
const showPageEditModal = ref(false)
const editingPage = ref<PageAdmin | null>(null)
const pageEditLabel = ref('')

function openPageEdit(p: PageAdmin) {
  editingPage.value = p
  pageEditLabel.value = p.label
  updateError.value = null
  showPageEditModal.value = true
}

function closePageEdit() {
  showPageEditModal.value = false
  editingPage.value = null
}

async function savePage() {
  if (!editingPage.value) return
  updating.value = true
  updateError.value = null
  try {
    const res = await store.updatePage(editingPage.value.uuid, { label: pageEditLabel.value })
    if (res.ok) {
      closePageEdit()
    } else {
      updateError.value = res.message
    }
  } finally {
    updating.value = false
  }
}

// ─── Auto-sync : appel direct + modal résultat lecture seule ────────────────
const autoSyncing = ref(false)
const showResultModal = ref(false)
const lastResult = ref<SyncResult | null>(null)
const lastResultError = ref<string | null>(null)

async function runAutoSync(dryRun: boolean) {
  if (!dryRun) {
    const ok = window.confirm(
      'Appliquer pour de vrai le sync depuis le code ?\n\n' +
      'Cela peut modifier les Features et Pages du registre (création, mise à jour, orphelinage).',
    )
    if (!ok) return
  }
  autoSyncing.value = true
  lastResultError.value = null
  try {
    const res = await store.autoSync(dryRun)
    if (res.ok) {
      lastResult.value = res.data
    } else {
      lastResultError.value = res.message
      lastResult.value = null
    }
    showResultModal.value = true
  } finally {
    autoSyncing.value = false
  }
}

function closeResultModal() {
  showResultModal.value = false
}

// ─── Sync manuel : modal avec textarea pour cas debug avancé ────────────────
const showSyncModal = ref(false)
const syncManifestText = ref('')
const syncing = ref(false)
const syncError = ref<string | null>(null)
const manualSyncResult = ref<SyncResult | null>(null)

function openSync() {
  syncManifestText.value = SAMPLE_MANIFEST()
  manualSyncResult.value = null
  syncError.value = null
  showSyncModal.value = true
}

function closeSync() {
  showSyncModal.value = false
  manualSyncResult.value = null
  syncError.value = null
}

async function runManualSync(dryRun: boolean) {
  syncError.value = null
  syncing.value = true
  try {
    let manifest: object
    try {
      manifest = JSON.parse(syncManifestText.value)
    } catch (e) {
      syncError.value = 'JSON invalide : ' + (e as Error).message
      syncing.value = false
      return
    }
    if (!dryRun) {
      const ok = window.confirm(
        '⚠ Appliquer ce manifest manuel pour de vrai ?\n\n' +
        'Toutes les Features/Pages non incluses dans ce manifest seront marquées orphelines.',
      )
      if (!ok) {
        syncing.value = false
        return
      }
    }
    const res = await store.runSync(manifest, dryRun)
    if (res.ok) {
      manualSyncResult.value = res.data
    } else {
      syncError.value = res.message
    }
  } finally {
    syncing.value = false
  }
}

function SAMPLE_MANIFEST(): string {
  return JSON.stringify(
    {
      source: 'test-manual',
      version: 'manual-' + Date.now(),
      features: [
        {
          key: 'billing.invoices',
          type: 'PAGE',
          label: 'Factures',
          requiredPermissions: ['PORTAL_BILLING'],
        },
        {
          key: 'support.tickets',
          type: 'PAGE',
          label: 'Tickets support',
          requiredPermissions: ['PORTAL_TICKETS'],
        },
      ],
      pages: [
        {
          key: 'invoices.list',
          label: 'Liste des factures',
          routePattern: '/entreprise?tab=invoices',
          matchType: 'PATH_AND_QUERY',
          matchValue: '/entreprise?tab=invoices',
          featureKey: 'billing.invoices',
          requiredPermissions: ['PORTAL_BILLING'],
        },
        {
          key: 'support.tickets.list',
          label: 'Liste des tickets',
          routePattern: '/support',
          matchType: 'PATH',
          matchValue: '/support',
          featureKey: 'support.tickets',
          requiredPermissions: ['PORTAL_TICKETS'],
        },
      ],
    },
    null,
    2,
  )
}

// ─── Badges ─────────────────────────────────────────────────────────────────
function typeBadgeClass(t: string) {
  if (t === 'PAGE') return 'badge-primary'
  if (t === 'CAPABILITY') return 'badge-accent'
  return 'badge-ghost'
}

function statusBadgeClass(s: FeatureStatus) {
  return s === 'ACTIVE' ? 'badge-success' : s === 'DEPRECATED' ? 'badge-warning' : 'badge-ghost'
}

onMounted(async () => {
  await Promise.all([store.fetchFeatures(), store.fetchPages()])
})
</script>

<template>
  <div class="p-6">
    <Bs2Title title="Feature Registry" subtitle="Référentiel fonctionnel de l'espace client" />

    <div class="flex flex-wrap gap-2 mt-4 items-center">
      <router-link to="/dashboard/admin" class="btn btn-outline btn-sm">← Retour dashboard admin</router-link>

      <div class="divider divider-horizontal mx-0" />

      <button
        class="btn btn-primary btn-sm gap-1"
        :disabled="autoSyncing"
        @click="runAutoSync(true)"
        title="Dry-run : voir le diff sans rien écrire en DB"
      >
        <ArrowPathIcon class="w-4 h-4" :class="autoSyncing ? 'animate-spin' : ''" />
        {{ autoSyncing ? 'Sync en cours…' : 'Auto-sync (preview)' }}
      </button>

      <button
        class="btn btn-success btn-sm gap-1"
        :disabled="autoSyncing"
        @click="runAutoSync(false)"
        title="Applique pour de vrai en DB"
      >
        <ArrowPathIcon class="w-4 h-4" />
        Auto-sync (appliquer)
      </button>

      <details class="dropdown dropdown-end">
        <summary class="btn btn-ghost btn-sm gap-1">
          Avancé ▾
        </summary>
        <ul class="menu dropdown-content bg-base-100 rounded-box z-10 w-64 p-2 shadow border border-base-300">
          <li>
            <button @click="openSync" class="text-left">
              <ExclamationTriangleIcon class="w-4 h-4 text-warning" />
              <div>
                <div class="font-semibold text-sm">Sync manuel (JSON)</div>
                <div class="text-xs text-base-content/60">
                  Pour debug avancé. Risque d'orphelinage massif si le JSON est partiel.
                </div>
              </div>
            </button>
          </li>
        </ul>
      </details>
    </div>

    <!-- KPI overview -->
    <div class="grid grid-cols-2 md:grid-cols-5 gap-3 mt-6">
      <div class="stat bg-base-100 shadow rounded-lg">
        <div class="stat-title text-xs">Features totales</div>
        <div class="stat-value text-2xl tabular-nums">{{ stats.total }}</div>
      </div>
      <div class="stat bg-base-100 shadow rounded-lg">
        <div class="stat-title text-xs">Pages</div>
        <div class="stat-value text-2xl tabular-nums text-primary">{{ stats.pages }}</div>
      </div>
      <div class="stat bg-base-100 shadow rounded-lg">
        <div class="stat-title text-xs">Capabilities</div>
        <div class="stat-value text-2xl tabular-nums text-accent">{{ stats.capabilities }}</div>
      </div>
      <div class="stat bg-base-100 shadow rounded-lg">
        <div class="stat-title text-xs">Umbrellas</div>
        <div class="stat-value text-2xl tabular-nums">{{ stats.umbrellas }}</div>
      </div>
      <div class="stat bg-base-100 shadow rounded-lg">
        <div class="stat-title text-xs">Orphans</div>
        <div class="stat-value text-2xl tabular-nums" :class="stats.orphans ? 'text-warning' : ''">
          {{ stats.orphans }}
        </div>
      </div>
    </div>

    <!-- Tabs + filters -->
    <div class="mt-6 card bg-base-100 shadow">
      <div class="card-body">
        <div class="flex items-center justify-between mb-4 gap-4 flex-wrap">
          <div role="tablist" class="tabs tabs-boxed">
            <button
              role="tab"
              class="tab"
              :class="{ 'tab-active': activeTab === 'features' }"
              @click="activeTab = 'features'"
            >
              <SquaresPlusIcon class="w-4 h-4 mr-1" />
              Features ({{ store.features.length }})
            </button>
            <button
              role="tab"
              class="tab"
              :class="{ 'tab-active': activeTab === 'pages' }"
              @click="activeTab = 'pages'"
            >
              <RectangleStackIcon class="w-4 h-4 mr-1" />
              Pages ({{ store.pages.length }})
            </button>
          </div>

          <div class="flex gap-2 items-center flex-wrap">
            <input
              v-model="searchFilter"
              type="text"
              placeholder="Rechercher key / label..."
              class="input input-bordered input-sm w-64"
            />
            <select v-if="activeTab === 'features'" v-model="featureTypeFilter" class="select select-bordered select-sm">
              <option value="">Tous types</option>
              <option value="PAGE">PAGE</option>
              <option value="CAPABILITY">CAPABILITY</option>
              <option value="UMBRELLA">UMBRELLA</option>
            </select>
            <select v-if="activeTab === 'features'" v-model="featureStatusFilter" class="select select-bordered select-sm">
              <option value="">Tous statuts</option>
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
              <option value="DEPRECATED">Deprecated</option>
            </select>
            <label class="label cursor-pointer gap-2">
              <input v-model="orphanOnlyFilter" type="checkbox" class="checkbox checkbox-sm" />
              <span class="label-text text-sm">Orphans uniquement</span>
            </label>
            <ButtonDaisy
              :text="activeTab === 'features' ? 'Actualiser' : 'Actualiser'"
              variant="outline"
              size="sm"
              @click="activeTab === 'features' ? store.fetchFeatures() : store.fetchPages()"
            />
          </div>
        </div>

        <!-- Features table -->
        <div v-if="activeTab === 'features'">
          <div v-if="store.isLoadingFeatures" class="py-8 text-center">
            <span class="loading loading-spinner loading-lg" />
          </div>
          <div v-else-if="filteredFeatures.length === 0" class="py-8 text-center text-base-content/60">
            Aucune feature trouvée. Lancez un sync pour les peupler depuis le code.
          </div>
          <div v-else class="overflow-x-auto">
            <table class="table table-sm">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Key / Label</th>
                  <th>Catégorie</th>
                  <th>Permissions</th>
                  <th>Service types</th>
                  <th class="text-center">Pages</th>
                  <th>Statut</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="f in filteredFeatures" :key="f.uuid" class="hover" :class="{ 'opacity-60': f.orphan }">
                  <td><span class="badge badge-sm" :class="typeBadgeClass(f.type)">{{ f.type }}</span></td>
                  <td>
                    <div class="font-mono text-xs font-semibold">{{ f.key }}</div>
                    <div class="text-sm text-base-content/80">{{ f.label }}</div>
                    <div v-if="f.capabilityEndpoint" class="text-xs text-base-content/50 font-mono">{{ f.capabilityEndpoint }}</div>
                  </td>
                  <td>
                    <span v-if="f.category" class="badge badge-ghost badge-sm">{{ f.category }}</span>
                    <span v-else class="text-base-content/40">—</span>
                  </td>
                  <td>
                    <div v-if="f.requiredPermissions.length" class="flex flex-wrap gap-1">
                      <span v-for="p in f.requiredPermissions" :key="p" class="badge badge-outline badge-xs font-mono">{{ p }}</span>
                    </div>
                    <span v-else class="text-base-content/40 text-xs">—</span>
                  </td>
                  <td>
                    <div v-if="f.serviceTypes.length" class="flex flex-wrap gap-1">
                      <span v-for="s in f.serviceTypes" :key="s" class="badge badge-outline badge-xs">{{ s }}</span>
                    </div>
                    <span v-else class="text-base-content/40 text-xs">—</span>
                  </td>
                  <td class="text-center tabular-nums">{{ f.pagesCount }}</td>
                  <td>
                    <div class="flex items-center gap-1">
                      <span class="badge badge-sm" :class="statusBadgeClass(f.status)">{{ f.status }}</span>
                      <span v-if="f.orphan" class="badge badge-warning badge-sm gap-1" title="N'est plus dans le code">
                        <ExclamationTriangleIcon class="w-3 h-3" />
                        orphan
                      </span>
                    </div>
                  </td>
                  <td>
                    <button class="btn btn-ghost btn-xs gap-1" @click="openFeatureEdit(f)">
                      <PencilSquareIcon class="w-3 h-3" />
                      Éditer
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Pages table -->
        <div v-if="activeTab === 'pages'">
          <div v-if="store.isLoadingPages" class="py-8 text-center">
            <span class="loading loading-spinner loading-lg" />
          </div>
          <div v-else-if="filteredPages.length === 0" class="py-8 text-center text-base-content/60">
            Aucune page trouvée.
          </div>
          <div v-else class="overflow-x-auto">
            <table class="table table-sm">
              <thead>
                <tr>
                  <th>Key / Label</th>
                  <th>Feature</th>
                  <th>Route</th>
                  <th>Match</th>
                  <th>Permissions</th>
                  <th>Statut</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="p in filteredPages" :key="p.uuid" class="hover" :class="{ 'opacity-60': p.orphan }">
                  <td>
                    <div class="font-mono text-xs font-semibold">{{ p.key }}</div>
                    <div class="text-sm text-base-content/80">{{ p.label }}</div>
                  </td>
                  <td>
                    <span v-if="p.featureKey" class="badge badge-ghost badge-sm font-mono">{{ p.featureKey }}</span>
                  </td>
                  <td class="font-mono text-xs text-base-content/70">{{ p.routePattern }}</td>
                  <td>
                    <span class="badge badge-outline badge-xs">{{ p.matchType }}</span>
                    <div class="font-mono text-xs text-base-content/60 mt-1">{{ p.matchValue }}</div>
                  </td>
                  <td>
                    <div v-if="p.requiredPermissions.length" class="flex flex-wrap gap-1">
                      <span v-for="perm in p.requiredPermissions" :key="perm" class="badge badge-outline badge-xs font-mono">{{ perm }}</span>
                    </div>
                    <span v-else class="text-base-content/40 text-xs">— (hérite)</span>
                  </td>
                  <td>
                    <span v-if="p.orphan" class="badge badge-warning badge-sm gap-1">
                      <ExclamationTriangleIcon class="w-3 h-3" />
                      orphan
                    </span>
                    <span v-else class="badge badge-success badge-sm">ACTIVE</span>
                  </td>
                  <td>
                    <button class="btn btn-ghost btn-xs gap-1" @click="openPageEdit(p)">
                      <PencilSquareIcon class="w-3 h-3" />
                      Éditer
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Feature Edit -->
    <div class="modal" :class="{ 'modal-open': showFeatureEditModal }">
      <div class="modal-box max-w-3xl">
        <h3 class="font-bold text-lg flex items-center gap-2">
          <CubeTransparentIcon class="w-6 h-6" />
          <span class="font-mono">{{ detail?.key ?? '…' }}</span>
        </h3>

        <div v-if="isLoadingDetail" class="py-8 text-center">
          <span class="loading loading-spinner loading-lg" />
        </div>

        <div v-else-if="detail" class="mt-4 space-y-4">
          <div class="form-control">
            <label class="label"><span class="label-text">Label utilisateur</span></label>
            <input v-model="editForm.label" type="text" class="input input-bordered input-sm" :disabled="updating" />
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div class="form-control">
              <label class="label"><span class="label-text">Catégorie</span></label>
              <input v-model="editForm.category" type="text" class="input input-bordered input-sm" :disabled="updating" />
            </div>
            <div class="form-control">
              <label class="label"><span class="label-text">Statut</span></label>
              <select v-model="editForm.status" class="select select-bordered select-sm" :disabled="updating">
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
                <option value="DEPRECATED">Deprecated</option>
              </select>
            </div>
          </div>

          <details class="collapse collapse-arrow bg-base-200">
            <summary class="collapse-title text-sm font-medium">Champs structurels (lecture seule, gérés par le sync)</summary>
            <div class="collapse-content text-xs space-y-2">
              <div><span class="font-semibold">Type :</span> {{ detail.type }}</div>
              <div><span class="font-semibold">Permissions :</span> <span class="font-mono">{{ detail.requiredPermissions.join(', ') || '—' }}</span></div>
              <div><span class="font-semibold">Service types :</span> <span class="font-mono">{{ detail.serviceTypes.join(', ') || '—' }}</span></div>
              <div><span class="font-semibold">Endpoint :</span> <span class="font-mono">{{ detail.capabilityEndpoint || '—' }}</span></div>
              <div><span class="font-semibold">Orphan :</span> {{ detail.orphan ? 'OUI' : 'non' }}</div>
            </div>
          </details>

          <div v-if="detail.pages.length" class="border-t pt-4">
            <h4 class="font-medium text-sm mb-2">Pages rattachées ({{ detail.pages.length }})</h4>
            <ul class="text-xs space-y-1">
              <li v-for="p in detail.pages" :key="p.uuid" class="font-mono">
                {{ p.key }} <span class="text-base-content/60">— {{ p.label }}</span>
                <span v-if="p.orphan" class="badge badge-warning badge-xs ml-2">orphan</span>
              </li>
            </ul>
          </div>

          <div v-if="updateError" class="alert alert-error text-sm">{{ updateError }}</div>
        </div>

        <div class="modal-action">
          <button class="btn" @click="closeFeatureEdit">Fermer</button>
          <button v-if="detail" class="btn btn-primary" :disabled="updating" @click="saveFeature">
            {{ updating ? 'Enregistrement…' : 'Enregistrer' }}
          </button>
        </div>
      </div>
      <div class="modal-backdrop" @click="closeFeatureEdit" />
    </div>

    <!-- Modal Page Edit -->
    <div class="modal" :class="{ 'modal-open': showPageEditModal }">
      <div class="modal-box max-w-md">
        <h3 class="font-bold text-lg">
          <span class="font-mono">{{ editingPage?.key }}</span>
        </h3>
        <div class="mt-4 space-y-4">
          <div class="form-control">
            <label class="label"><span class="label-text">Label utilisateur</span></label>
            <input v-model="pageEditLabel" type="text" class="input input-bordered input-sm" :disabled="updating" />
          </div>
          <div v-if="updateError" class="alert alert-error text-sm">{{ updateError }}</div>
        </div>
        <div class="modal-action">
          <button class="btn" @click="closePageEdit">Annuler</button>
          <button class="btn btn-primary" :disabled="updating" @click="savePage">
            {{ updating ? 'Enregistrement…' : 'Enregistrer' }}
          </button>
        </div>
      </div>
      <div class="modal-backdrop" @click="closePageEdit" />
    </div>

    <!-- Modal Résultat (auto-sync : lecture seule) -->
    <div class="modal" :class="{ 'modal-open': showResultModal }">
      <div class="modal-box max-w-2xl">
        <h3 class="font-bold text-lg flex items-center gap-2">
          <ArrowPathIcon class="w-6 h-6 text-primary" />
          Résultat de l'auto-sync
        </h3>

        <div v-if="lastResultError" class="alert alert-error mt-4">
          {{ lastResultError }}
        </div>

        <div v-else-if="lastResult" class="mt-4 space-y-4">
          <div class="alert" :class="lastResult.dryRun ? 'alert-info' : 'alert-success'">
            <div>
              <strong>{{ lastResult.dryRun ? 'Mode dry-run' : 'Sync appliqué ✓' }}</strong>
              <div class="text-xs opacity-80">
                Source : {{ lastResult.source ?? 'auto-sync' }} ·
                {{ new Date(lastResult.executedAt).toLocaleString('fr-FR') }}
              </div>
            </div>
          </div>

          <!-- Features -->
          <div>
            <h4 class="font-semibold text-sm mb-2 flex items-center gap-2">
              <SquaresPlusIcon class="w-4 h-4" />
              Features
            </h4>
            <div class="space-y-1 text-xs">
              <div v-if="lastResult.features.created.length">
                <span class="badge badge-success badge-sm">+{{ lastResult.features.created.length }} créées</span>
                <span class="font-mono ml-2 text-base-content/70">
                  {{ lastResult.features.created.map((e) => e.key).slice(0, 5).join(', ') }}{{ lastResult.features.created.length > 5 ? '…' : '' }}
                </span>
              </div>
              <div v-if="lastResult.features.updated.length">
                <span class="badge badge-info badge-sm">↻ {{ lastResult.features.updated.length }} mises à jour</span>
              </div>
              <div v-if="lastResult.features.reactivated.length">
                <span class="badge badge-success badge-sm">↑ {{ lastResult.features.reactivated.length }} réactivées</span>
                <span class="font-mono ml-2 text-base-content/70">
                  {{ lastResult.features.reactivated.map((e) => e.key).slice(0, 5).join(', ') }}{{ lastResult.features.reactivated.length > 5 ? '…' : '' }}
                </span>
              </div>
              <div v-if="lastResult.features.orphaned.length">
                <span class="badge badge-warning badge-sm">⚠ {{ lastResult.features.orphaned.length }} orphelinées</span>
                <span class="font-mono ml-2 text-base-content/70">
                  {{ lastResult.features.orphaned.map((e) => e.key).slice(0, 5).join(', ') }}{{ lastResult.features.orphaned.length > 5 ? '…' : '' }}
                </span>
              </div>
              <div
                v-if="!lastResult.features.created.length && !lastResult.features.updated.length
                  && !lastResult.features.reactivated.length && !lastResult.features.orphaned.length"
                class="text-base-content/50"
              >
                Aucun changement.
              </div>
            </div>
          </div>

          <!-- Pages -->
          <div>
            <h4 class="font-semibold text-sm mb-2 flex items-center gap-2">
              <RectangleStackIcon class="w-4 h-4" />
              Pages
            </h4>
            <div class="space-y-1 text-xs">
              <div v-if="lastResult.pages.created.length">
                <span class="badge badge-success badge-sm">+{{ lastResult.pages.created.length }} créées</span>
                <span class="font-mono ml-2 text-base-content/70">
                  {{ lastResult.pages.created.map((e) => e.key).slice(0, 5).join(', ') }}{{ lastResult.pages.created.length > 5 ? '…' : '' }}
                </span>
              </div>
              <div v-if="lastResult.pages.updated.length">
                <span class="badge badge-info badge-sm">↻ {{ lastResult.pages.updated.length }} mises à jour</span>
              </div>
              <div v-if="lastResult.pages.reactivated.length">
                <span class="badge badge-success badge-sm">↑ {{ lastResult.pages.reactivated.length }} réactivées</span>
                <span class="font-mono ml-2 text-base-content/70">
                  {{ lastResult.pages.reactivated.map((e) => e.key).slice(0, 5).join(', ') }}{{ lastResult.pages.reactivated.length > 5 ? '…' : '' }}
                </span>
              </div>
              <div v-if="lastResult.pages.orphaned.length">
                <span class="badge badge-warning badge-sm">⚠ {{ lastResult.pages.orphaned.length }} orphelinées</span>
                <span class="font-mono ml-2 text-base-content/70">
                  {{ lastResult.pages.orphaned.map((e) => e.key).slice(0, 5).join(', ') }}{{ lastResult.pages.orphaned.length > 5 ? '…' : '' }}
                </span>
              </div>
              <div
                v-if="!lastResult.pages.created.length && !lastResult.pages.updated.length
                  && !lastResult.pages.reactivated.length && !lastResult.pages.orphaned.length"
                class="text-base-content/50"
              >
                Aucun changement.
              </div>
            </div>
          </div>

          <!-- Action si dry-run : proposer d'appliquer -->
          <div v-if="lastResult.dryRun" class="alert alert-info text-sm">
            Ceci est un aperçu. Pour appliquer, ferme cette fenêtre et clique sur <strong>« Auto-sync (appliquer) »</strong>.
          </div>
        </div>

        <div class="modal-action">
          <button class="btn btn-primary" @click="closeResultModal">Fermer</button>
        </div>
      </div>
      <div class="modal-backdrop" @click="closeResultModal" />
    </div>

    <!-- Modal Sync manuel (debug / cas avancé) -->
    <div class="modal" :class="{ 'modal-open': showSyncModal }">
      <div class="modal-box max-w-5xl">
        <h3 class="font-bold text-lg flex items-center gap-2">
          <ExclamationTriangleIcon class="w-6 h-6 text-warning" />
          Sync manuel du Feature Registry
        </h3>
        <p class="text-xs text-base-content/60 mt-2">
          Saisis ou colle un manifest JSON. Utilise <strong>Dry-run</strong> pour voir le diff sans appliquer.
        </p>

        <div class="alert alert-warning mt-4 text-sm">
          <ExclamationTriangleIcon class="w-5 h-5" />
          <div>
            <strong>Attention :</strong> ce sync remplacera l'état actuel par celui du JSON ci-dessous.
            Tout ce qui n'y figure pas sera marqué <strong>orphan</strong>. Préférez « Auto-sync » pour
            le cas standard. Ce mode manuel est réservé au debug avancé.
          </div>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
          <div class="form-control">
            <label class="label"><span class="label-text">Manifest JSON (exemple, à remplacer)</span></label>
            <textarea
              v-model="syncManifestText"
              class="textarea textarea-bordered font-mono text-xs"
              rows="20"
              :disabled="syncing"
            />
          </div>

          <div class="space-y-3">
            <div v-if="syncError" class="alert alert-error text-sm">{{ syncError }}</div>

            <div v-if="manualSyncResult">
              <div class="alert" :class="manualSyncResult.dryRun ? 'alert-info' : 'alert-success'">
                <div>
                  <strong>{{ manualSyncResult.dryRun ? 'Dry-run' : 'Appliqué' }}</strong>
                  — {{ new Date(manualSyncResult.executedAt).toLocaleString('fr-FR') }}
                </div>
              </div>

              <div class="mt-3 space-y-3">
                <div>
                  <h4 class="font-semibold text-sm mb-1">Features</h4>
                  <div class="text-xs space-y-1">
                    <div v-if="manualSyncResult.features.created.length">
                      <span class="badge badge-success badge-sm">+{{ manualSyncResult.features.created.length }} créées</span>
                      <span class="font-mono ml-2">{{ manualSyncResult.features.created.map((e) => e.key).join(', ') }}</span>
                    </div>
                    <div v-if="manualSyncResult.features.updated.length">
                      <span class="badge badge-info badge-sm">↻{{ manualSyncResult.features.updated.length }} maj</span>
                      <span class="font-mono ml-2">{{ manualSyncResult.features.updated.map((e) => e.key).join(', ') }}</span>
                    </div>
                    <div v-if="manualSyncResult.features.reactivated.length">
                      <span class="badge badge-success badge-sm">↑{{ manualSyncResult.features.reactivated.length }} réactivées</span>
                      <span class="font-mono ml-2">{{ manualSyncResult.features.reactivated.map((e) => e.key).join(', ') }}</span>
                    </div>
                    <div v-if="manualSyncResult.features.orphaned.length">
                      <span class="badge badge-warning badge-sm">⚠{{ manualSyncResult.features.orphaned.length }} orphans</span>
                      <span class="font-mono ml-2">{{ manualSyncResult.features.orphaned.map((e) => e.key).join(', ') }}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 class="font-semibold text-sm mb-1">Pages</h4>
                  <div class="text-xs space-y-1">
                    <div v-if="manualSyncResult.pages.created.length">
                      <span class="badge badge-success badge-sm">+{{ manualSyncResult.pages.created.length }} créées</span>
                      <span class="font-mono ml-2">{{ manualSyncResult.pages.created.map((e) => e.key).join(', ') }}</span>
                    </div>
                    <div v-if="manualSyncResult.pages.updated.length">
                      <span class="badge badge-info badge-sm">↻{{ manualSyncResult.pages.updated.length }} maj</span>
                      <span class="font-mono ml-2">{{ manualSyncResult.pages.updated.map((e) => e.key).join(', ') }}</span>
                    </div>
                    <div v-if="manualSyncResult.pages.reactivated.length">
                      <span class="badge badge-success badge-sm">↑{{ manualSyncResult.pages.reactivated.length }} réactivées</span>
                      <span class="font-mono ml-2">{{ manualSyncResult.pages.reactivated.map((e) => e.key).join(', ') }}</span>
                    </div>
                    <div v-if="manualSyncResult.pages.orphaned.length">
                      <span class="badge badge-warning badge-sm">⚠{{ manualSyncResult.pages.orphaned.length }} orphans</span>
                      <span class="font-mono ml-2">{{ manualSyncResult.pages.orphaned.map((e) => e.key).join(', ') }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-action">
          <button class="btn" @click="closeSync">Fermer</button>
          <button class="btn btn-info gap-2" :disabled="syncing" @click="runManualSync(true)">
            <PlayIcon class="w-4 h-4" />
            {{ syncing ? '...' : 'Dry-run' }}
          </button>
          <button class="btn btn-warning gap-2" :disabled="syncing" @click="runManualSync(false)">
            <ArrowPathIcon class="w-4 h-4" />
            {{ syncing ? 'Sync…' : 'Appliquer ce JSON' }}
          </button>
        </div>
      </div>
      <div class="modal-backdrop" @click="closeSync" />
    </div>
  </div>
</template>
